export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: any,
  ) {
    super(message);
  }
}

export class OfflineError extends Error {
  constructor() {
    super('No connection');
  }
}

export function deviceId(): string {
  const KEY = 'civilos.deviceId';
  let id = '';
  try {
    id = localStorage.getItem(KEY) ?? '';
    if (!id) {
      id = 'web-' + crypto.randomUUID().replace(/-/g, '').slice(0, 24);
      localStorage.setItem(KEY, id);
    }
  } catch {
    id = 'web-ephemeral-' + Math.random().toString(36).slice(2, 12);
  }
  return id;
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: 'same-origin',
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(method !== 'GET' ? { 'X-CivilOS-Request': '1' } : {}),
        'X-Device-Id': deviceId(),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new OfflineError();
  }
  const ct = res.headers.get('content-type') ?? '';
  const data = ct.includes('json') ? await res.json() : null;
  if (!res.ok) {
    const e = data?.error ?? {};
    if (res.status === 401 && !url.startsWith('/api/auth/login')) window.dispatchEvent(new CustomEvent('civilos:unauthorized'));
    throw new ApiError(res.status, e.code ?? 'ERROR', e.message ?? `Request failed (${res.status})`, e.details);
  }
  return data as T;
}

export const api = {
  get: <T = any>(url: string) => request<T>('GET', url),
  post: <T = any>(url: string, body: unknown = {}) => request<T>('POST', url, body),
  patch: <T = any>(url: string, body: unknown = {}) => request<T>('PATCH', url, body),
};

export async function fileToBase64(file: File): Promise<string> {
  const buf = new Uint8Array(await file.arrayBuffer());
  let bin = '';
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(bin);
}
